/*
 * The row the player is typing into is a fixed length array of slots, one per tile, where
 * an empty string means "nothing entered yet". A plain string cannot express that: with a
 * cursor the player may drop a known letter on the fourth tile while the second one is
 * still blank.
 *
 * Everything in here is pure and works on copies, so the board can hand the result straight
 * back into its change detection.
 */
export interface WordGuessInputState {
  input: string[];
  cursorIndex: number;
}

export function createWordGuessInput(wordLength: number): string[] {
  return Array.from({ length: Math.max(0, wordLength) }, () => '');
}

/*
 * Moves the cursor by whole tiles and stops at both ends of the row. It deliberately does
 * not wrap around: a cursor that jumps from the last tile back to the first one reads as a
 * glitch when the player only wanted to step past the end.
 */
export function moveWordGuessCursor(cursorIndex: number, offset: number, wordLength: number): number {
  if (wordLength <= 0) {
    return 0;
  }

  return Math.min(wordLength - 1, Math.max(0, cursorIndex + offset));
}

/*
 * Writes a letter into the slot under the cursor, replacing whatever was there, and moves
 * the cursor to the next slot that is still empty. Skipping filled slots is what makes
 * typing around already placed letters feel natural: with _ _ E _ _ and the cursor at the
 * start, two letters land on the first two tiles and the third keystroke goes behind the E.
 *
 * Once no empty slot is left to the right the cursor just steps one tile and stays inside
 * the row, so the last letter can be corrected by typing over it.
 */
export function writeWordGuessLetter(input: string[], cursorIndex: number, letter: string): WordGuessInputState {
  if (!letter || cursorIndex < 0 || cursorIndex >= input.length) {
    return { input, cursorIndex };
  }

  const next = [...input];
  next[cursorIndex] = letter;

  return { input: next, cursorIndex: nextWritableIndex(next, cursorIndex) };
}

/*
 * Clears the slot under the cursor if it holds a letter, otherwise the one in front of it
 * and takes the cursor along. That is what a caret in a text field does, and it keeps a
 * single Backspace from ever deleting two letters.
 */
export function deleteWordGuessLetter(input: string[], cursorIndex: number): WordGuessInputState {
  if (cursorIndex >= 0 && cursorIndex < input.length && input[cursorIndex]) {
    const next = [...input];
    next[cursorIndex] = '';

    return { input: next, cursorIndex };
  }

  const target = Math.min(input.length, Math.max(0, cursorIndex)) - 1;

  if (target < 0) {
    return { input, cursorIndex: moveWordGuessCursor(cursorIndex, 0, input.length) };
  }

  const next = [...input];
  next[target] = '';

  return { input: next, cursorIndex: target };
}

/* True only when every tile of the row carries a letter — an incomplete row is no guess. */
export function isWordGuessInputComplete(input: string[]): boolean {
  return input.length > 0 && input.every((letter) => !!letter);
}

/* The word as submitted. Only meaningful for a complete row, see isWordGuessInputComplete. */
export function wordGuessInputToWord(input: string[]): string {
  return input.join('');
}

/* True when at least one tile of the row carries a letter, i.e. there is something to delete. */
export function hasWordGuessInput(input: string[]): boolean {
  return input.some((letter) => !!letter);
}

function nextWritableIndex(input: string[], cursorIndex: number): number {
  for (let index = cursorIndex + 1; index < input.length; index++) {
    if (!input[index]) {
      return index;
    }
  }

  return Math.min(input.length - 1, cursorIndex + 1);
}
